import { type FC, memo } from "react";
import { Pressable, View, useWindowDimensions, Platform } from "react-native";
import Animated, { FadeIn, LinearTransition, useAnimatedStyle, withTiming } from "react-native-reanimated";
import { useUnistyles, StyleSheet } from "react-native-unistyles";
import { Text } from "../Text";
import { sizes } from "@src/constants/sizes";
import { Icon, IconNameType } from "../Icon";

type SegmentedControlProps = {
  optionsLabels: {
    title: string;
    icon?: IconNameType;
  }[];
  selectedOption: string;
  onSelectOption: (option: string) => void;
  width?: number;
};

const IconSize = 20;
const ContainerHeight = 36;

const SegmentedControl: FC<SegmentedControlProps> = memo(({ optionsLabels, selectedOption, onSelectOption, width }) => {
  const { theme } = useUnistyles();
  const optionsTitles = optionsLabels.map((option) => option.title);
  const { width: windowWidth } = useWindowDimensions();
  const internalPadding = theme.spacing.md;

  // On web, account for max-width constraint
  const availableWidth = Platform.OS === "web" ? Math.min(windowWidth, sizes.maxWidthWeb) : windowWidth;
  const segmentedControlWidth = width ?? availableWidth - theme.spacing["5xl"] * 2;
  const selectedItemWidth = (segmentedControlWidth - internalPadding) / optionsLabels.length;

  const rStyle = useAnimatedStyle(() => {
    const selectedIndex = optionsTitles.indexOf(selectedOption);
    const leftPosition = optionsTitles.slice(0, selectedIndex).reduce((acc) => acc + selectedItemWidth, 0);

    return {
      left: withTiming(leftPosition + internalPadding / 2, { duration: 300 }),
      width: withTiming(selectedItemWidth, { duration: 300 }),
    };
  }, [selectedOption, selectedItemWidth, optionsTitles]);

  return (
    <View style={styles.outerContainer}>
      <View
        style={[
          styles.container,
          {
            width: segmentedControlWidth,
            paddingHorizontal: internalPadding / 2,
          },
        ]}
      >
        <Animated.View style={[styles.activeBox, rStyle]} />
        {optionsLabels.map((option, index) => (
          <Pressable
            onPress={() => onSelectOption(option.title)}
            key={index}
            style={[
              styles.row,
              {
                width: selectedItemWidth,
              },
            ]}
          >
            <Animated.View
              style={[
                styles.textRow,
                {
                  justifyContent: "center",
                  gap: theme.spacing.md,
                },
              ]}
              layout={LinearTransition.duration(350)}
            >
              {option.icon && <Icon name={option.icon} size={IconSize} color={theme.colors.text} />}

              <Animated.View entering={FadeIn.duration(500)} layout={LinearTransition.duration(350)}>
                <Text textStyle="sm/medium" style={styles.title}>
                  {option.title}
                </Text>
              </Animated.View>
            </Animated.View>
          </Pressable>
        ))}
      </View>
    </View>
  );
});

SegmentedControl.displayName = "SegmentedControl";

const styles = StyleSheet.create((theme) => ({
  outerContainer: {},
  container: {
    flexDirection: "row",
    height: ContainerHeight,
    backgroundColor: theme.colors.border,
    borderRadius: ContainerHeight / 2,
    borderCurve: "continuous",
  },
  row: {
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "row",
  },
  activeBox: {
    position: "absolute",
    top: "10%",
    height: "80%",
    backgroundColor: theme.colors.background,
    borderRadius: ContainerHeight / 2,
    borderCurve: "continuous",
  },
  textRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  title: {
    color: theme.colors.text,
    textAlign: "center",
  },
}));

export default SegmentedControl;
